import { Suspense, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  LogOut,
  UserCircle2,
  ChevronRight,
  Inbox,
  Ellipsis,
  ExternalLink,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { cn } from '@reflex/ui/lib/utils';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { useAuth } from '@/hooks/auth/useAuth';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { useInboxCount } from '@/hooks/ui/useInboxCount';
import type { NavItem } from './nav-types';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { useAccountMenuItems } from './account-nav';
import { useConfigureEntry } from '../ConfigureShell';
import { NavUtilityElements } from './NavUtilityElements';

const MAX_INBOX_COUNT = 99;

const THEME_TOGGLE_OPTIONS = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
] as const;

/**
 * Stable tutorial hook for a user-menu entry, derived from its route so the
 * intro tour can spotlight specific items (Notifications, Send feedback)
 * without coupling to their visible labels. Strips any org-slug prefix, hash,
 * and query, then keys off the final path segment — e.g.
 * `/orgs/acme/notifications` → `user-menu-item-notifications` and
 * `#help-inspector` → `user-menu-item-help-inspector`. The built-in Account
 * row hard-codes `user-menu-item-account`: it is a door to the takeover, not
 * a nav item with a segment worth deriving from.
 */
function userMenuItemTutorialId(to: string): string {
  const withoutHash = to.startsWith('#') ? to.slice(1) : to;
  const path = withoutHash.split(/[?#]/)[0] ?? withoutHash;
  const segment = path.replace(/\/+$/, '').split('/').filter(Boolean).pop() ?? '';
  return `user-menu-item-${segment || 'item'}`;
}

/**
 * Inbox button rendered alongside the account avatar. Hosting it here keeps
 * the count one click away from the user's identity (Slack/Linear pattern)
 * and frees the main nav for destination-style entries.
 */
/** Badged inbox link shared by the rail and the sessions dock header. */
export function InboxLink({ slug, compact = false }: { slug: string; compact?: boolean }) {
  const count = useInboxCount();
  const hasCount = count > 0;
  const display = count > MAX_INBOX_COUNT ? `${MAX_INBOX_COUNT}+` : String(count);
  const label = hasCount ? `Inbox (${count} ${count === 1 ? 'item' : 'items'})` : 'Open inbox';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={ROUTES.org(slug).inbox}
          data-testid="user-section-inbox"
          aria-label={label}
          className={cn(
            'group/inbox relative flex shrink-0 items-center gap-1 rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
            compact ? 'h-8 w-8 justify-center' : 'px-1.5 py-1.5',
          )}
        >
          <Inbox className="h-4 w-4" />
          {!compact && hasCount && (
            <span className="text-2xs font-medium tabular-nums">{display}</span>
          )}
          {compact && hasCount && (
            <span
              data-testid="user-section-inbox-dot"
              className="pointer-events-none absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary"
            />
          )}
        </Link>
      </TooltipTrigger>
      <TooltipContent side={compact ? 'right' : 'top'}>{label}</TooltipContent>
    </Tooltip>
  );
}

function UserAvatar({ login, avatarUrl }: { login: string; avatarUrl?: string | null }) {
  const [imgError, setImgError] = useState(false);

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={`${login}'s avatar`}
        className="h-7 w-7 rounded-full object-cover shrink-0 ring-1 ring-border/40"
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted/60 text-xs font-medium text-muted-foreground shrink-0 ring-1 ring-border/40">
      {login.charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Wrapper for a plugin-contributed trailing control. Most trailing slots are
 * inert (an unread count), but a plugin may put a real control there — its own
 * link or button beside a row that does something else. A click that lands on
 * a real control inside the slot belongs to that control alone: it must not
 * also fire the row's command, and because stopping it there would leave the
 * dropdown open over the new page, the menu closes here instead. Clicks on
 * inert badge text still fall through to the row, so a count next to
 * "Notifications" keeps opening Notifications.
 */
function TrailingSlot({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  return (
    <span
      className="ml-auto flex shrink-0 items-center"
      onClick={(event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('a,button')) return;
        event.stopPropagation();
        onClose?.();
      }}
    >
      {children}
    </span>
  );
}

function UserMenuContent({
  pluginItems,
  configureTo,
  onClose,
}: {
  pluginItems: ReadonlyArray<NavItem>;
  /** Landing path for the Configure gear (remembered section). */
  configureTo: string;
  /** Closes the dropdown when a plugin's trailing control handled the click. */
  onClose?: () => void;
}) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  return (
    <>
      {/* Header: user info + settings gear + theme toggle */}
      <div className="flex flex-col gap-4 px-4 pt-4 pb-3.5">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.profile}
            onClick={onClose}
            data-testid="user-menu-profile-link"
            className="flex flex-1 min-w-0 items-center gap-2 -mx-1 rounded-md px-1 py-1 transition-colors hover:bg-accent"
          >
            {user && <UserAvatar login={user.login} avatarUrl={user.avatarUrl} />}
            <div className="flex min-w-0 flex-1 flex-col items-start gap-px">
              <PanelTitle as="span" size="sm" className="truncate w-full">
                {user?.login}
              </PanelTitle>
              {user?.email && (
                <span className="truncate w-full text-xs text-muted-foreground">{user.email}</span>
              )}
            </div>
          </Link>
          <Link
            to={configureTo}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Configure"
            data-testid="user-menu-configure"
          >
            <Settings className="h-4 w-4" />
          </Link>
        </div>

        {/* Light / Dark / System toggle */}
        <div className="flex items-center gap-[3px] rounded-[9px] border border-sidebar-border bg-sidebar p-[3px]">
          {THEME_TOGGLE_OPTIONS.map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              aria-label={label}
              aria-pressed={theme === value}
              onClick={() => setTheme(value)}
              className={cn(
                'flex flex-1 items-center justify-center rounded-[6px] px-[11px] py-1.5 transition-colors',
                theme === value
                  ? 'bg-accent text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      <DropdownMenuSeparator className="mx-0 my-0" />

      {/*
       * One row per destination: icon + label + optional trailing control.
       * Account is the door to the takeover; the menu keeps only what that
       * surface cannot hold — actions and live-state rows.
       */}
      <div>
        <DropdownMenuItem
          asChild
          data-tutorial-id="user-menu-item-account"
          className="h-10 cursor-pointer gap-3 rounded-none px-4"
        >
          <Link to={ROUTES.profile}>
            <span className="flex flex-1 min-w-0 items-center gap-2">
              <UserCircle2 className="h-4 w-4 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-sm font-medium">Account</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        {pluginItems.map(({ to, label, icon: Icon, trailing: Trailing, openInNewWindow }) => {
          if (to.startsWith('#')) {
            return (
              <DropdownMenuItem
                key={to}
                data-tutorial-id={userMenuItemTutorialId(to)}
                className="h-10 cursor-pointer gap-3 rounded-none px-4"
                onSelect={() => {
                  setTimeout(
                    () => window.dispatchEvent(new CustomEvent('plugin:command', { detail: to })),
                    0,
                  );
                }}
              >
                <span className="flex flex-1 min-w-0 items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm font-medium">{label}</span>
                </span>
                {Trailing && (
                  <TrailingSlot onClose={onClose}>
                    <Suspense fallback={null}>
                      <Trailing />
                    </Suspense>
                  </TrailingSlot>
                )}
              </DropdownMenuItem>
            );
          }
          if (openInNewWindow) {
            return (
              <DropdownMenuItem
                key={to}
                asChild
                data-tutorial-id={userMenuItemTutorialId(to)}
                className="h-10 cursor-pointer gap-3 rounded-none px-4"
              >
                <a
                  href={to}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${label} (opens in new window)`}
                >
                  <span className="flex flex-1 min-w-0 items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-sm font-medium">{label}</span>
                  </span>
                  <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                </a>
              </DropdownMenuItem>
            );
          }
          return (
            <DropdownMenuItem
              key={to}
              asChild
              data-tutorial-id={userMenuItemTutorialId(to)}
              className="h-10 cursor-pointer gap-3 rounded-none px-4"
            >
              <Link to={to}>
                <span className="flex flex-1 min-w-0 items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm font-medium">{label}</span>
                </span>
                {Trailing && (
                  <TrailingSlot onClose={onClose}>
                    <Suspense fallback={null}>
                      <Trailing />
                    </Suspense>
                  </TrailingSlot>
                )}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </div>

      <DropdownMenuSeparator className="mx-0 my-0" />

      <DropdownMenuItem
        className="h-10 cursor-pointer gap-3 rounded-none px-4"
        onClick={() => void logout()}
      >
        <span className="flex flex-1 min-w-0 items-center gap-2">
          <LogOut className="h-4 w-4 shrink-0" />
          <span className="flex-1 min-w-0 truncate text-sm font-medium">Log Out</span>
        </span>
      </DropdownMenuItem>
    </>
  );
}

export function UserSection({ collapsed, bar }: { collapsed?: boolean; bar?: boolean }) {
  const { authEnabled, user } = useAuth();
  const slug = useActiveOrgSlug();
  const configureEntry = useConfigureEntry();
  const [menuOpen, setMenuOpen] = useState(false);
  // The intro tour opens this menu to show it off, then asks us to close it
  // before moving the spotlight up to the org switcher by stamping
  // `data-tutorial-close-user-menu` on <html> (see the intro tour's
  // htmlSignals). React to that DOM signal rather than importing the tutorial
  // plugin so the boundary stays clean.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => {
      if (root.getAttribute('data-tutorial-close-user-menu') === 'true') {
        setMenuOpen(false);
      }
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-tutorial-close-user-menu'],
    });
    return () => observer.disconnect();
  }, []);
  const accountMenuItems = useAccountMenuItems();
  if (!authEnabled) return null;

  const hasOrgSlug = !!slug && slug !== '_';

  // The dock's bottom bar: the account menu opening upward from the row's
  // left corner.
  if (bar) {
    return (
      <div className="flex shrink-0 items-center gap-0.5">
        {user && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full p-0"
                aria-label="Account menu"
              >
                <UserAvatar login={user.login} avatarUrl={user.avatarUrl} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="top"
              className="w-60 p-0 rounded-xl overflow-hidden"
              data-tutorial-id="user-menu-content"
            >
              <UserMenuContent
                pluginItems={accountMenuItems}
                configureTo={configureEntry.to}
                onClose={() => setMenuOpen(false)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1.5">
        {hasOrgSlug && <InboxLink slug={slug} compact />}
        <NavUtilityElements compact />
        {user && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full p-0"
                aria-label="Account menu"
              >
                <UserAvatar login={user.login} avatarUrl={user.avatarUrl} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              side="right"
              className="w-60 p-0 rounded-xl overflow-hidden"
              data-tutorial-id="user-menu-content"
            >
              <UserMenuContent
                pluginItems={accountMenuItems}
                configureTo={configureEntry.to}
                onClose={() => setMenuOpen(false)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <div className="flex w-full items-center gap-1 px-1.5">
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 rounded-lg px-1.5 py-1.5 text-left transition-all hover:bg-sidebar-accent/40"
            aria-label="Account menu"
          >
            {user && <UserAvatar login={user.login} avatarUrl={user.avatarUrl} />}
            <PanelTitle as="span" size="sm" className="min-w-0 text-sidebar-foreground">
              {user?.login}
            </PanelTitle>
          </button>
        </DropdownMenuTrigger>
        {hasOrgSlug && <InboxLink slug={slug} />}
        <NavUtilityElements />
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open account menu"
          className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground/50 transition-colors hover:bg-sidebar-accent/40 hover:text-muted-foreground"
        >
          <Ellipsis className="h-4 w-4" />
        </button>
      </div>
      <DropdownMenuContent
        align="start"
        side="top"
        className="w-60 p-0 rounded-xl overflow-hidden"
        data-tutorial-id="user-menu-content"
      >
        <UserMenuContent
          pluginItems={accountMenuItems}
          configureTo={configureEntry.to}
          onClose={() => setMenuOpen(false)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
